import React, { useEffect, useRef, useState, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { localStorageKeys } from '../../utils/constants';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';

// ─────────────────────────────────────────────
// Config
// ─────────────────────────────────────────────
const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024; // 10 MB

// Kept generic — works for any merchant screen
const ISSUE_TYPES = [
  { value: 'not_working', label: "Something isn't working" },
  { value: 'crash', label: 'App crashed or froze' },
  { value: 'display', label: 'Something looks off' },
  { value: 'slow', label: 'Slow or not loading' },
  { value: 'wrong_data', label: 'Wrong or missing info' },
  { value: 'other', label: 'Other' },
];

const FREQUENCIES = [
  { value: 'once', label: 'Just once' },
  { value: 'sometimes', label: 'Sometimes' },
  { value: 'always', label: 'Every time' },
];

const initialForm = {
  type: '',
  screen: '',
  frequency: '',
  description: '',
  email: '',
  contact: true,
};

// ─────────────────────────────────────────────
// Styles (light theme)
// ─────────────────────────────────────────────
const css = `
.rb { --navy:#121A2C; --orange:#FF7B29; --orange-text:#f3722cff; --orange-tint:#FFF6EF; --orange-line:#F0A877;
  --bg:#F4F5F8; --card:#FFFFFF; --line:#E6E8EE; --input:#D5D9E2; --text:#121A2C; --text2:#3A4256; --muted:#5B6275; --error:#B42318;
  min-height: 100vh; background: var(--bg); color: var(--text); font-family: Inter, system-ui, -apple-system, sans-serif; }
.rb *, .rb *::before, .rb *::after { box-sizing: border-box; }
.rb :focus-visible { outline: 2px solid var(--orange-text); outline-offset: 2px; }
.rb-sr { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0 0 0 0); white-space:nowrap; border:0; }

/* Header bar stays full width; its content lines up with the form column below
   (.rb-wrap = centered 760px with 20px side padding → content starts at (100% - 760px)/2 + 20px) */
.rb-top { height:64px; background:var(--card); border-bottom:1px solid var(--line); display:flex; align-items:center; justify-content:flex-start; gap:16px; padding:0 max(20px, calc((100% - 760px) / 2 + 20px)); }
.rb-top__brand { font-size:20px; font-weight:700; }
.rb-top__title { display:none; font-size:17px; font-weight:700; }
.rb-back { display:inline-flex; align-items:center; gap:6px; min-height:44px; margin-left:-4px; padding:0 4px; border:0; background:none; color:var(--orange-text); font:600 14px Inter, system-ui, sans-serif; cursor:pointer; }
.rb-back:hover { color:#8A3508; }

.rb-wrap { max-width:760px; margin:0 auto; padding:48px 20px 64px; }
.rb-intro h1 { margin:0 0 8px; font-size:32px; font-weight:700; letter-spacing:-0.01em; }
.rb-intro p { margin:0 0 28px; font-size:16px; line-height:1.5; color:var(--text2); }
.rb-card { background:var(--card); border:1px solid var(--line); border-radius:16px; padding:32px; }
.rb-form { display:flex; flex-direction:column; gap:28px; }

.rb fieldset { border:0; margin:0; padding:0; min-width:0; }
.rb-label, .rb legend { display:block; padding:0; margin-bottom:10px; font-size:15px; font-weight:600; }
.rb-opt { font-weight:400; color:var(--muted); }
.rb-hint { display:block; margin:-4px 0 8px; font-size:14px; color:var(--muted); }
.rb-field { display:flex; flex-direction:column; }
.rb-row2 { display:grid; grid-template-columns:repeat(auto-fit, minmax(260px, 1fr)); gap:24px; }

.rb-chips { display:flex; flex-wrap:wrap; gap:8px; }
.rb-chip input, .rb-seg input { position:absolute; opacity:0; width:1px; height:1px; }
.rb-chip span { min-height:44px; display:inline-flex; align-items:center; gap:6px; padding:0 16px; border-radius:999px; border:1px solid var(--input); background:var(--card); color:#2A3145; font-size:14px; font-weight:500; cursor:pointer; user-select:none; }
.rb-chip svg { display:none; }
.rb-chip input:checked + span { background:var(--navy); border-color:var(--navy); color:#fff; }
.rb-chip input:checked + span svg { display:block; }
.rb-chip input:focus-visible + span, .rb-seg input:focus-visible + span { outline:2px solid var(--orange-text); outline-offset:2px; }

.rb-seg { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:4px; padding:4px; background:var(--line); border-radius:12px; }
.rb-seg label { position:relative; }
.rb-seg span { height:40px; display:flex; align-items:center; justify-content:center; border-radius:9px; font-size:13px; font-weight:500; color:var(--text2); cursor:pointer; }
.rb-seg input:checked + span { background:var(--card); color:var(--text); font-weight:600; box-shadow:0 1px 2px rgba(18,26,44,.12); }

.rb input[type="text"], .rb input[type="email"], .rb textarea { width:100%; border:1px solid var(--input); border-radius:10px; background:var(--card); color:var(--text); font:16px Inter, system-ui, sans-serif; }
.rb input[type="text"], .rb input[type="email"] { height:48px; padding:0 14px; }
.rb input[type="email"] { max-width:420px; }
.rb textarea { min-height:140px; padding:12px 14px; line-height:1.5; resize:vertical; }
.rb [aria-invalid="true"] { border-color:var(--error); }
.rb-error { margin-top:8px; font-size:13px; font-weight:500; color:var(--error); }

.rb-check { display:flex; align-items:center; gap:10px; min-height:44px; margin-top:4px; font-size:14px; color:#2A3145; cursor:pointer; }
.rb-check input { width:18px; height:18px; margin:0; accent-color:var(--navy); }

.rb-shots-head { display:flex; justify-content:space-between; align-items:baseline; margin-bottom:12px; }
.rb-shots-head .rb-label { margin:0; }
.rb-count { font-size:14px; color:var(--muted); }
.rb-drop { position:relative; display:flex; flex-direction:column; align-items:center; gap:8px; text-align:center; padding:28px 20px; border:1.5px dashed var(--orange-line); border-radius:12px; background:var(--orange-tint); cursor:pointer; }
.rb-drop.is-over { background:#FFE9D9; border-color:var(--orange); }
.rb-drop:focus-within { outline:2px solid var(--orange-text); outline-offset:2px; }
.rb-drop__icon { width:44px; height:44px; border-radius:999px; background:var(--card); color:var(--orange-text); display:flex; align-items:center; justify-content:center; }
.rb-drop__main { font-size:15px; color:#2A3145; }
.rb-drop__main u { color:var(--orange-text); font-weight:600; text-underline-offset:2px; }
.rb-drop__sub { font-size:13px; color:var(--muted); }

.rb-thumbs { display:grid; grid-template-columns:repeat(auto-fill, minmax(120px, 1fr)); gap:12px; margin:12px 0 0; padding:0; list-style:none; }
.rb-thumb { position:relative; aspect-ratio:1; border-radius:10px; overflow:hidden; background:var(--line); border:1px solid var(--input); }
.rb-thumb img { width:100%; height:100%; object-fit:cover; display:block; }
.rb-thumb__rm { position:absolute; top:0; right:0; width:44px; height:44px; display:flex; align-items:flex-start; justify-content:flex-end; padding:6px; border:0; background:transparent; cursor:pointer; }
.rb-thumb__rm span { width:24px; height:24px; border-radius:999px; background:var(--navy); color:#fff; display:flex; align-items:center; justify-content:center; }

.rb-foot { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:16px; padding-top:24px; border-top:1px solid var(--line); }
.rb-foot__note { flex:1 1 280px; display:flex; gap:8px; align-items:center; font-size:13px; color:var(--muted); }
.rb-btn { height:48px; padding:0 28px; display:inline-flex; align-items:center; justify-content:center; gap:8px; border-radius:12px; font:700 15px Inter, system-ui, sans-serif; cursor:pointer; text-decoration:none; }
.rb-btn--primary { border:0; background:var(--orange); color:var(--navy); }
.rb-btn--primary:hover { background:#F06A16; }
.rb-btn--primary:disabled { opacity:.6; cursor:progress; }
.rb-btn--ghost { border:1px solid var(--input); background:var(--card); color:var(--text); font-weight:600; }
/* Error alert shown above the form */
.rb-alert { display:flex; gap:12px; align-items:flex-start; margin:0 0 20px; padding:14px 16px; background:#FEF3F2; border:1px solid #FECDCA; border-radius:12px; color:var(--error); scroll-margin-top:90px; }
.rb-alert__icon { flex-shrink:0; margin-top:1px; }
.rb-alert__body { flex:1; min-width:0; }
.rb-alert__title { display:block; margin-bottom:2px; font-size:14px; font-weight:700; }
.rb-alert__text { margin:0; font-size:14px; line-height:1.45; color:#7A271A; }
.rb-submit-error { width:100%; margin:0; font-size:14px; color:var(--error); }
.rb-alert__close { flex-shrink:0; width:32px; height:32px; margin:-6px -8px -6px 0; display:flex; align-items:center; justify-content:center; border:0; background:transparent; color:var(--error); cursor:pointer; border-radius:8px; }

.rb-success { display:flex; flex-direction:column; align-items:center; gap:16px; padding:64px 32px; text-align:center; }
.rb-success__icon { width:80px; height:80px; border-radius:999px; background:#FFF3EA; color:var(--orange-text); display:flex; align-items:center; justify-content:center; }
.rb-success h1 { margin:0; font-size:28px; }
.rb-success p { margin:0; max-width:460px; font-size:16px; line-height:1.55; color:var(--text2); }
.rb-success__id { font-size:13px; color:var(--muted); background:var(--line); border-radius:999px; padding:6px 12px; }
.rb-success__actions { display:flex; flex-wrap:wrap; justify-content:center; gap:12px; margin-top:12px; }

.rb-reward { display:flex; gap:14px; align-items:flex-start; margin:0 0 28px; padding:16px 18px; background:#FFF3EA; border:1px solid #F5D3BB; border-radius:14px; text-align:left; }
.rb-reward__icon { flex-shrink:0; width:40px; height:40px; border-radius:999px; background:var(--orange); color:var(--navy); display:flex; align-items:center; justify-content:center; }
.rb-reward__title { display:block; margin-bottom:2px; font-size:15px; font-weight:700; color:var(--text); }
.rb-reward__text { margin:0 !important; font-size:14px !important; line-height:1.5; color:var(--text2) !important; max-width:none !important; }
.rb-success .rb-reward { max-width:460px; margin:4px 0 0; }

@media (max-width: 600px) {
  .rb-top { height:56px; padding:0 8px; justify-content:flex-start; gap:4px; }
  .rb-top__brand, .rb-back__text { display:none; }
  .rb-top__title { display:block; }
  .rb-back { width:44px; justify-content:center; margin-left:0; }
  .rb-wrap { padding:20px 16px 32px; }
  .rb-intro h1 { display:none; }
  .rb-intro p { font-size:14px; margin-bottom:20px; }
  .rb-card { padding:0; border:0; background:transparent; border-radius:0; }
  .rb-form { gap:26px; }
  .rb-label, .rb legend { font-size:14px; }
  .rb-hint { font-size:13px; }
  .rb-chip span { font-size:13px; padding:0 14px; }
  .rb-seg span { height:44px; }
  .rb-thumbs { grid-template-columns:repeat(3, minmax(0,1fr)); gap:10px; }
  .rb-drop { padding:18px 16px; }
  .rb-drop__drag { display:none; }
  .rb input[type="email"] { max-width:none; }
  .rb-foot { border-top:0; padding-top:0; }
  .rb-foot__note { background:var(--card); border:1px solid var(--line); border-radius:10px; padding:12px 14px; color:var(--text2); }
  .rb-form .rb-btn--primary { width:100%; height:52px; font-size:16px; }
  .rb-success { padding:56px 8px; }
  .rb-success__actions { flex-direction:column; width:100%; }
  .rb-success__actions .rb-btn { width:100%; height:52px; }
  .rb-reward { padding:14px; gap:12px; margin-bottom:20px; }
  .rb-reward__icon { width:36px; height:36px; }
}
`;

// ─────────────────────────────────────────────
// Icons
// ─────────────────────────────────────────────
const Svg = ({ children, size = 18, sw = 2, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth={sw}
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
    {...rest}
  >
    {children}
  </svg>
);
const CheckIcon = (p) => (
  <Svg {...p}>
    <path d='M20 6 9 17l-5-5' />
  </Svg>
);
const BackIcon = () => (
  <Svg sw={2.2}>
    <path d='M15 18l-6-6 6-6' />
  </Svg>
);
const UploadIcon = () => (
  <Svg size={22}>
    <path d='M12 16V4' />
    <path d='M7 9l5-5 5 5' />
    <path d='M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3' />
  </Svg>
);
const XIcon = () => (
  <Svg size={12} sw={3}>
    <path d='M18 6 6 18M6 6l12 12' />
  </Svg>
);
const InfoIcon = () => (
  <Svg size={16} style={{ flexShrink: 0 }}>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 16v-4M12 8h.01' />
  </Svg>
);
const SendIcon = () => (
  <Svg size={17} sw={2.2}>
    <path d='M22 2 11 13' />
    <path d='M22 2l-7 20-4-9-9-4z' />
  </Svg>
);

const AlertIcon = () => (
  <Svg size={18} className='rb-alert__icon'>
    <circle cx='12' cy='12' r='9' />
    <path d='M12 8v5M12 16h.01' />
  </Svg>
);

const HeartIcon = () => (
  <Svg size={20}>
    <path d='M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1-1.1a5.5 5.5 0 0 0-7.8 7.8l1 1.1L12 21l7.8-7.5 1-1.1a5.5 5.5 0 0 0 0-7.8z' />
  </Svg>
);

// Highlighted reward callout
const RewardNote = ({ title, children }) => (
  <div className='rb-reward'>
    <span className='rb-reward__icon'>
      <HeartIcon />
    </span>
    <div>
      <strong className='rb-reward__title'>{title}</strong>
      <p className='rb-reward__text'>{children}</p>
    </div>
  </div>
);

/**
 * Report a bug — merchant / kitchen web & mobile.
 *
 * Props:
 *  - source:     "merchant_web" | "merchant_mobile"   (default "merchant_web")
 *  - merchantId: optional — sent so the report is linked to the store
 *  - appVersion: string sent with the report
 *  - onBack:     called by the back button and "Back to SingleCombo" (default: history.back())
 *
 * Page URL: navigate here with  navigate('/report-bug', { state: { from: window.location.href } })
 * so we capture the screen the merchant was actually on (SPA routes don't update document.referrer).
 */

const { FINGER_PRINT_USER } = localStorageKeys;
export default function ReportUiBug({
  source = 'merchant_web',
  merchantId = '',
  appVersion = 'web-beta',
  onBack,
}) {
  const { onReportUiBugCustomer } = useContext(GlobalStateDataContext) || {};
  const location = useLocation();
  const [form, setForm] = useState(initialForm);
  const [files, setFiles] = useState([]); // { id, file, url }
  const [fileError, setFileError] = useState('');
  const [screenError, setScreenError] = useState(false);
  const [descError, setDescError] = useState(false);
  const [submitError, setSubmitError] = useState(''); // local problems — shown under the button
  const [apiError, setApiError] = useState(''); // errors returned by the API — shown on top
  const [sending, setSending] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [reportId, setReportId] = useState(null); // set on success

  const screenRef = useRef(null);
  const descRef = useRef(null);
  const successRef = useRef(null);
  const alertRef = useRef(null);
  const filesRef = useRef(files);
  filesRef.current = files;

  // Revoke preview URLs on unmount
  useEffect(() => () => filesRef.current.forEach((f) => URL.revokeObjectURL(f.url)), []);

  useEffect(() => {
    if (reportId !== null && successRef.current) successRef.current.focus();
  }, [reportId]);

  // Bring the API error alert into view when it appears
  useEffect(() => {
    if (apiError && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [apiError]);

  const goBack = () => (onBack ? onBack() : window.history.back());

  const update = (key) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (key === 'description' && value.trim()) setDescError(false);
    if (key === 'screen' && value.trim()) setScreenError(false);
  };

  // ───── Attachments ─────
  const addFiles = (list) => {
    const incoming = Array.from(list || []);
    if (!incoming.length) return;

    const errors = [];
    const next = [...filesRef.current];
    incoming.forEach((file) => {
      if (!file.type || !file.type.startsWith('image/'))
        return errors.push(`${file.name} isn't an image`);
      if (file.size > MAX_SIZE) return errors.push(`${file.name} is over 10 MB`);
      if (next.length >= MAX_FILES) return errors.push(`You can attach up to ${MAX_FILES} images`);
      next.push({ id: `${Date.now()}-${Math.random()}`, file, url: URL.createObjectURL(file) });
    });
    filesRef.current = next;
    setFiles(next);
    setFileError([...new Set(errors)].join('. '));
  };

  const removeFile = (id) => {
    setFiles((current) =>
      current.filter((f) => {
        if (f.id === id) URL.revokeObjectURL(f.url);
        return f.id !== id;
      })
    );
    setFileError('');
  };

  // Paste screenshots from clipboard (desktop)
  useEffect(() => {
    const onPaste = (e) => {
      if (reportId !== null) return;
      const pasted = e.clipboardData && e.clipboardData.files;
      if (pasted && pasted.length) addFiles(pasted);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [reportId]);

  // ───── Submit ─────
  const DEFAULT_SUBMIT_ERROR = 'Something went wrong sending your report. Please try again.';
  const gettingFingerPrint = () => localStorage.getItem(FINGER_PRINT_USER);

  // Builds the multipart payload the backend expects
  const buildReportPayload = () => {
    const data = new FormData();
    data.append('source', source);
    data.append('type', form.type);
    data.append('screen', form.screen.trim());
    data.append('frequency', form.frequency);
    data.append('description', form.description.trim());
    data.append('email', form.email.trim());
    data.append('contact', form.contact ? 'yes' : 'no');
    if (merchantId) data.append('merchantId', merchantId);
    files.forEach((f) => data.append('images', f.file, f.file.name));

    // Auto-collected
    data.append('deviceId', gettingFingerPrint() || '');
    // Screen they came from (passed via navigate state), falling back to referrer
    data.append('pageUrl', location.state?.from || document.referrer || '');
    data.append('reportPageUrl', window.location.href);
    data.append('userAgent', navigator.userAgent);
    data.append('viewport', `${window.innerWidth}x${window.innerHeight}`);
    data.append('language', navigator.language || '');
    data.append('appVersion', appVersion);
    data.append('reportedAt', new Date().toISOString());

    return data;
  };

  // Pulls a readable message out of any error shape (axios, fetch, plain Error, 429 from rate limiter)
  const getErrorMessage = (err) => {
    if (!err) return DEFAULT_SUBMIT_ERROR;
    if (typeof err === 'string') return err;
    const status = err?.response?.status || err?.status;
    if (status === 429)
      return 'Too many reports in a short time. Please try again in a few minutes.';
    if (status === 413) return 'Your images are too large. Please attach smaller screenshots.';
    if (err?.message === 'Network Error' || err?.name === 'TypeError') {
      return "We couldn't reach our servers. Check your internet connection and try again.";
    }
    return (
      err?.response?.data?.message || err?.data?.message || err?.message || DEFAULT_SUBMIT_ERROR
    );
  };

  // Sends the full payload to onReportUiBugCustomer — expects { success, message, id }
  const SubmitReportBug = async () => {
    if (typeof onReportUiBugCustomer !== 'function') {
      setSubmitError('Reporting is unavailable right now. Please try again later.');
      return;
    }

    setSending(true);
    try {
      const response = await onReportUiBugCustomer(buildReportPayload());

      // Works whether the API returns the raw axios response or response.data
      const result = response?.data ?? response;

      if (!result || result.success === false) {
        setApiError(result?.message || DEFAULT_SUBMIT_ERROR);
        return;
      }

      setReportId(result.id || '');
      window.scrollTo(0, 0);
    } catch (err) {
      setApiError(getErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitError('');
    setApiError('');

    const missingScreen = !form.screen.trim();
    const missingDesc = !form.description.trim();
    setScreenError(missingScreen);
    setDescError(missingDesc);

    if (missingScreen) {
      if (screenRef.current) screenRef.current.focus();
      return;
    }
    if (missingDesc) {
      if (descRef.current) descRef.current.focus();
      return;
    }

    SubmitReportBug();
  };

  const reset = () => {
    files.forEach((f) => URL.revokeObjectURL(f.url));
    setFiles([]);
    setForm(initialForm);
    setFileError('');
    setScreenError(false);
    setDescError(false);
    setSubmitError('');
    setApiError('');
    setReportId(null);
    window.scrollTo(0, 0);
  };

  const sent = reportId !== null;

  return (
    <div className='rb'>
      <style>{css}</style>

      <header className='rb-top'>
        <button type='button' className='rb-back' onClick={goBack} aria-label='Back to Home'>
          <BackIcon />
          <span className='rb-back__text'>Back to Home</span>
        </button>
        <span className='rb-top__title' aria-hidden='true'>
          Report a bug
        </span>
      </header>

      <div className='rb-wrap'>
        {!sent ? (
          <>
            <div className='rb-intro'>
              <h1>Report a bug</h1>
              <p>
                Sorry you hit a snag. Tell us what went wrong — the more detail you share, the
                faster we can fix it.
              </p>
              <RewardNote title='Thanks for helping us build SingleCombo'>
                You're one of our first partners, and your feedback directly shapes what we fix and
                build next. Every report goes straight to our team, and we'll follow up if we need
                more details.
              </RewardNote>
            </div>

            {/* API errors only */}
            {apiError && (
              <div className='rb-alert' ref={alertRef} role='alert' aria-live='assertive'>
                <AlertIcon />
                <div className='rb-alert__body'>
                  <strong className='rb-alert__title'>We couldn't send your report</strong>
                  <p className='rb-alert__text'>{apiError}</p>
                </div>
                <button
                  type='button'
                  className='rb-alert__close'
                  onClick={() => setApiError('')}
                  aria-label='Dismiss error'
                >
                  <XIcon />
                </button>
              </div>
            )}

            <form className='rb-card rb-form' onSubmit={handleSubmit} noValidate>
              {/* Issue type */}
              <fieldset>
                <legend>What kind of issue?</legend>
                <div className='rb-chips'>
                  {ISSUE_TYPES.map((t) => (
                    <label className='rb-chip' key={t.value}>
                      <input
                        type='radio'
                        name='type'
                        value={t.value}
                        checked={form.type === t.value}
                        onChange={update('type')}
                      />
                      <span>
                        <CheckIcon size={14} sw={2.6} />
                        {t.label}
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Screen + frequency */}
              <div className='rb-row2'>
                <div className='rb-field'>
                  <label className='rb-label' htmlFor='rb-screen'>
                    Which screen were you on?
                  </label>
                  <input
                    id='rb-screen'
                    ref={screenRef}
                    type='text'
                    maxLength={200}
                    value={form.screen}
                    onChange={update('screen')}
                    aria-invalid={screenError ? 'true' : undefined}
                    aria-describedby={screenError ? 'rb-screen-error' : undefined}
                    placeholder='e.g. Menu editor, Orders, Payouts, Store hours'
                  />
                  {screenError && (
                    <span className='rb-error' id='rb-screen-error' role='alert'>
                      Please tell us which screen you were on.
                    </span>
                  )}
                </div>

                <fieldset>
                  <legend>How often does it happen?</legend>
                  <div className='rb-seg'>
                    {FREQUENCIES.map((f) => (
                      <label key={f.value}>
                        <input
                          type='radio'
                          name='frequency'
                          value={f.value}
                          checked={form.frequency === f.value}
                          onChange={update('frequency')}
                        />
                        <span>{f.label}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              </div>

              {/* Description */}
              <div className='rb-field'>
                <label className='rb-label' htmlFor='rb-desc'>
                  What happened?
                </label>
                <span className='rb-hint' id='rb-desc-hint'>
                  What were you trying to do, and what went wrong?
                </span>
                <textarea
                  id='rb-desc'
                  ref={descRef}
                  rows={6}
                  maxLength={3000}
                  value={form.description}
                  onChange={update('description')}
                  aria-invalid={descError ? 'true' : undefined}
                  aria-describedby={descError ? 'rb-desc-hint rb-desc-error' : 'rb-desc-hint'}
                  placeholder='e.g. I changed the price on an item, clicked Save, and the old price came back.'
                />
                {descError && (
                  <span className='rb-error' id='rb-desc-error' role='alert'>
                    Please tell us what happened so we can look into it.
                  </span>
                )}
              </div>

              {/* Screenshots */}
              <div className='rb-field'>
                <div className='rb-shots-head'>
                  <span className='rb-label' id='rb-shots-label'>
                    Screenshots <span className='rb-opt'>(optional)</span>
                  </span>
                  <span className='rb-count'>
                    {files.length} of {MAX_FILES}
                  </span>
                </div>

                {files.length < MAX_FILES && (
                  <label
                    className={`rb-drop${dragOver ? ' is-over' : ''}`}
                    onDragEnter={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDragOver(true);
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      addFiles(e.dataTransfer && e.dataTransfer.files);
                    }}
                  >
                    <span className='rb-drop__icon'>
                      <UploadIcon />
                    </span>
                    <span className='rb-drop__main'>
                      <span className='rb-drop__drag'>Drag &amp; drop images here, or </span>
                      <u>browse</u> to add images
                    </span>
                    <span className='rb-drop__sub'>
                      Up to {MAX_FILES} images · PNG, JPG, WEBP or HEIC · 10 MB each
                    </span>
                    <input
                      type='file'
                      className='rb-sr'
                      accept='image/*'
                      multiple
                      aria-labelledby='rb-shots-label'
                      onChange={(e) => {
                        addFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                  </label>
                )}

                {fileError && (
                  <span className='rb-error' role='alert'>
                    {fileError}.
                  </span>
                )}

                {files.length > 0 && (
                  <ul className='rb-thumbs' aria-label='Attached screenshots'>
                    {files.map((f) => (
                      <li className='rb-thumb' key={f.id}>
                        <img src={f.url} alt={f.file.name} />
                        <button
                          type='button'
                          className='rb-thumb__rm'
                          onClick={() => removeFile(f.id)}
                          aria-label={`Remove ${f.file.name}`}
                        >
                          <span>
                            <XIcon />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Contact */}
              <div className='rb-field'>
                <label className='rb-label' htmlFor='rb-email'>
                  Email <span className='rb-opt'>(optional)</span>
                </label>
                <input
                  id='rb-email'
                  type='email'
                  autoComplete='email'
                  placeholder='you@example.com'
                  value={form.email}
                  onChange={update('email')}
                />
                <label className='rb-check'>
                  <input type='checkbox' checked={form.contact} onChange={update('contact')} />
                  It's OK to contact me about this report
                </label>
              </div>

              {/* Footer */}
              <div className='rb-foot'>
                <span className='rb-foot__note'>
                  <InfoIcon />
                  Your device, browser and the page you were on are attached automatically.
                </span>
                <button type='submit' className='rb-btn rb-btn--primary' disabled={sending}>
                  <SendIcon />
                  {sending ? 'Sending…' : 'Send report'}
                </button>
                {submitError && (
                  <p className='rb-submit-error' role='alert'>
                    {submitError}
                  </p>
                )}
              </div>
            </form>
          </>
        ) : (
          <section className='rb-card rb-success' ref={successRef} tabIndex={-1} aria-live='polite'>
            <div className='rb-success__icon' aria-hidden='true'>
              <CheckIcon size={38} sw={2.4} />
            </div>
            <h1>Thanks — we got it!</h1>
            <p>
              Thanks for taking the time to report this. Our team will review it and work on a fix
              as quickly as we can.
            </p>
            <RewardNote title='We appreciate you'>
              {form.email.trim()
                ? "We'll reach out by email if we need more details or once it's fixed."
                : 'Add your email next time so we can update you when it’s fixed.'}
            </RewardNote>
            {reportId && <span className='rb-success__id'>Report ID: {reportId}</span>}
            <div className='rb-success__actions'>
              <button type='button' className='rb-btn rb-btn--primary' onClick={goBack}>
                Back to SingleCombo
              </button>
              <button type='button' className='rb-btn rb-btn--ghost' onClick={reset}>
                Report another issue
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
